import { AuthData } from "@/shared/types/auth";
import { ApiAuthenticatedUserResponse } from "@/shared/types/user";

import { useAuthStore } from "@/store/auth.store";
// import {
//     authorizeInSocketIo,
// initIoEvents
// IO,
// ioConnect,
// ioDisconnect
// } from "./websocket.service";
import { httpRequest } from "./http";
import { useTypedRouter } from "@/composables/typed-router.composable";
import { RouteNameLiteral } from "@/router/types";
import { ApiAuthRefreshTokenResponse, ApiAuthRevokeTokenResponse } from "@/modules/auth/auth.types";
import { displayMessage } from "@/store/message.store";
import { LOCK_KEY } from "@/constants";
import momentTz from "@/helpers/moment";

let logout_promise: null | Promise<void> = null;
let refreshing_auth_data_promise: null | Promise<void> = null;

export async function logUserIn(ad: AuthData, initial_app_boot: boolean = false): Promise<void> {
    const AUTH_STORE = useAuthStore();

    AUTH_STORE.setAuthData(ad);

    try {
        await fetchAuthenticatedUserData(initial_app_boot);
    } catch (err) {
        AUTH_STORE.unsetAuthState();
        throw err;
    }

    // if (!IO.connected) {
    //     initIoEvents();
    //     try {
    //         await ioConnect();
    //         await authorizeInSocketIo();
    //     } catch (err) {
    //         console.error(err);
    //     }
    // } else {
    //     initIoEvents();
    //     await authorizeInSocketIo();
    // }
}

export async function fetchAuthenticatedUserData(initial_app_boot: boolean = false): Promise<void> {
    const AUTH_STORE = useAuthStore();

    if (!AUTH_STORE.is_logged_in) {
        throw new Error(
            "You need to set AuthData before calling fetchAuthenticatedUserData() method"
        );
    }

    const r = await httpRequest<ApiAuthenticatedUserResponse>(
        { method: "GET", url: `/users/me` },
        {
            await_for_app_boot: initial_app_boot !== true,
            handle_unauthorized: initial_app_boot ? "never" : "logout_user"
        }
    );
    AUTH_STORE.setUser(r.data.user);
    AUTH_STORE.setUserDevice(r.data.user_device.device);
    AUTH_STORE.setUserPermissions(r.data.user_permissions);

    return;
}

export type LogUserOutOpts = {
    alert_type?: "error" | "info";
    alert_msg?: string;
    logout_from_all_devices?: boolean;
    redirect_route_name?: RouteNameLiteral;
};
export function logUserOut(opts?: LogUserOutOpts): Promise<void> {
    const AUTH_STORE = useAuthStore();
    const ROUTER = useTypedRouter();

    if (logout_promise !== null) {
        return logout_promise;
    }
    logout_promise = new Promise(async resolve => {
        ROUTER.push({
            name:
                opts && opts.redirect_route_name ? opts.redirect_route_name : "dashboard-auth-login"
        });

        const AD = AUTH_STORE.auth_data;
        if (AD) {
            try {
                await httpRequest<ApiAuthRevokeTokenResponse>({
                    method: "POST",
                    url: `/auth/revoke-token`,
                    data: {
                        access_token: AD.access_token,
                        refresh_token: AD.refresh_token,
                        logout_from_all_devices: opts && opts.logout_from_all_devices === true
                    }
                });
            } catch (err) {
                console.error(err);
            }
        }

        AUTH_STORE.unsetAuthState();

        // Poniżej należy zarejestrować kod, który ma zostać wykonany przy wylogowaniu (np. reset dodatkowego state)
        // TODO: reset ostatnio wybranej marki
        // END

        // if (IO.connected) {
        //     ioDisconnect();
        // }

        displayMessage({
            type: opts && opts.alert_type ? opts.alert_type : "error",
            msg: "Wylogowano pomyślnie"
        });

        logout_promise = null;
        return resolve();
    });
    return logout_promise!;
}

export function refreshAuthData(ad: AuthData): Promise<void> {
    if (refreshing_auth_data_promise != null) {
        return refreshing_auth_data_promise;
    }

    refreshing_auth_data_promise = new Promise(async (resolve, reject) => {
        window.navigator.locks.request(LOCK_KEY.REFRESH_DATA_LOCK, async function (lock) {
            if (!lock) {
                return reject(`Could not acquire an exclusive lock on given resource`);
            }

            const AUTH_STORE = useAuthStore();
            if (!AUTH_STORE.auth_data) {
                refreshing_auth_data_promise = null;
                return reject(`AuthData must be set before trying to refresh the access token`);
            }

            if (AUTH_STORE.auth_data.access_token !== ad.access_token) {
                refreshing_auth_data_promise = null;
                return resolve();
            }

            try {
                const RES = await httpRequest<ApiAuthRefreshTokenResponse>(
                    {
                        method: "POST",
                        url: `/auth/refresh-token`,
                        data: ad
                    },
                    {
                        auth: "never"
                    }
                );

                if (RES.data.success) {
                    await AUTH_STORE.setAuthData(RES.data.data);

                    // if (IO.connected) {
                    //     try {
                    //         await authorizeInSocketIo();
                    //     } catch (err) {
                    //         console.error(err);
                    //     }
                    // }
                } else {
                    return reject(`Could not refresh access_token: ${JSON.stringify(RES.data)}`);
                }
            } catch (err) {
                refreshing_auth_data_promise = null;
                return reject(err);
            }

            refreshing_auth_data_promise = null;
            return resolve();
        });
    });
    return refreshing_auth_data_promise;
}

export function isValidAuthData(obj: any): obj is AuthData {
    if (!obj || typeof obj !== "object") return false;

    if (
        obj.access_token !== undefined &&
        typeof obj.access_token === "string" &&
        obj.access_token != "" &&
        obj.refresh_token !== undefined &&
        typeof obj.refresh_token === "string" &&
        obj.refresh_token != "" &&
        obj.access_token_exp_date !== undefined &&
        typeof obj.access_token_exp_date === "string" &&
        momentTz(obj.access_token_exp_date, momentTz.ISO_8601, true).isValid() &&
        obj.refresh_token_exp_date !== undefined &&
        typeof obj.refresh_token_exp_date === "string" &&
        momentTz(obj.refresh_token_exp_date, momentTz.ISO_8601, true).isValid()
    ) {
        return true;
    }

    return false;
}
